import {ComponentRef, ViewRef} from '@angular/core';

import {Observable, of, Subject, zip} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

import {DbModalBackdropComponent} from './modal-backdrop';
import {DbModalWindowComponent} from './modal-window';

function isPromise<T>(v: any): v is Promise<T> {
  return v && v.then;
}

export class ContentRef {
  constructor(public nodes: any[], public viewRef?: ViewRef, public componentRef?: ComponentRef<any>) {
  }
}

/**
 * A reference to the currently opened (active) modal.
 *
 * Instances of this class can be injected into your component passed as modal content.
 * So you can `.close()` or `.dismiss()` the modal window from your component.
 */
export class DbActiveModal {
  setBackdrop(backdrop: boolean | 'static'): void {
  }

  hideCloseBtn(hide: boolean): void {
  }

  hideOutsideCloseBtn(hide: boolean): void {
  }

  hasBorder(show: boolean): void {
  }

  overflowHidden(show: boolean): void {
  }

  whiteCloseBtn(white: boolean, opacity?: number): void {
  }

  /**
   * Resize modal
   * @param height
   * @param width
   */
  resize(height: number | string, width: number): void {
  }

  /**
   * Closes the modal with an optional `result` value.
   *
   * The `DbModalRef.result` promise will be resolved with the provided value.
   */
  close(result?: any): void {
  }

  /**
   * Dismisses the modal with an optional `reason` value.
   *
   * The `DbModalRef.result` promise will be rejected with the provided value.
   */
  dismiss(reason?: any): void {
  }
}

/**
 * A reference to the newly opened modal returned by the `DbModalService.open()` method.
 */
export class DbModalRef {
  private _closed = new Subject<any>();
  private _dismissed = new Subject<any>();
  private _hidden = new Subject<void>();
  private _resolve: (result?: any) => void;
  private _reject: (reason?: any) => void;


  /**
   * The instance of a component used for the modal content.
   *
   * When a `TemplateRef` is used as the content or when the modal is closed, will return `undefined`.
   */
  get componentInstance(): any {
    if (this._contentRef && this._contentRef.componentRef) {
      return this._contentRef.componentRef.instance;
    }
  }

  /**
   * The instance of a component used for the modal content.
   *
   * When a `TemplateRef` is used as the content or when the modal is closed, will return `undefined`.
   */
  set componentInstance(params) {
    if (this._contentRef && this._contentRef.componentRef) {
      for (const key in params) {
        this._contentRef.componentRef.instance[key] = params[key];
      }
    }
  }

  /**
   * The promise that is resolved when the modal is closed and rejected when the modal is dismissed.
   */
  result: Promise<any>;

  /**
   * The observable that emits when the modal is closed via the `.close()` method.
   *
   * It will emit the result passed to the `.close()` method.
   * */
  get closed(): Observable<any> {
    return this._closed.asObservable().pipe(takeUntil(this._hidden));
  }

  /**
   * The observable that emits when the modal is dismissed via the `.dismiss()` method.
   *
   * It will emit the reason passed to the `.dismissed()` method by the user, or one of the internal
   * reasons like backdrop click or ESC key press.
   */
  get dismissed(): Observable<any> {
    return this._dismissed.asObservable().pipe(takeUntil(this._hidden));
  }

  /**
   * The observable that emits when both modal window and backdrop are closed and animations were finished.
   * At this point modal and backdrop elements will be removed from the DOM tree.
   *
   * This observable will be completed after emitting.
   */
  get hidden(): Observable<void> {
    return this._hidden.asObservable();
  }

  constructor(
    private _windowCmptRef: ComponentRef<DbModalWindowComponent>, private _contentRef: ContentRef,
    private _backdropCmptRef?: ComponentRef<DbModalBackdropComponent>,
    private _beforeDismiss?: () => boolean | Promise<boolean>) {
    _windowCmptRef.instance.dismissEvent.subscribe((reason: any) => {
      reason = this.componentInstance?.modalClosedResult !== undefined && !reason ? this.componentInstance.modalClosedResult : reason;
      this.dismiss(reason);
    });

    this.result = new Promise((resolve, reject) => {
      this._resolve = resolve;
      this._reject = reject;
    });
    this.result.then(null, () => {});
  }

  setBackdrop(backdrop: boolean | 'static'): void {
    if (this._windowCmptRef && this._windowCmptRef.instance) {
      this._windowCmptRef.instance.backdrop = backdrop;
    }
  }

  hideCloseBtn(hide: boolean = false): void {
    if (this._windowCmptRef && this._windowCmptRef.instance) {
      this._windowCmptRef.instance.hasCloseBtn = !hide;
    }
  }
    hideOutsideCloseBtn(hide: boolean = false): void {
        if (this._windowCmptRef && this._windowCmptRef.instance) {
            this._windowCmptRef.instance.hasOutsideCloseBtn = !hide;
        }
    }

  hasBorder(show: boolean = false): void {
    if (this._windowCmptRef && this._windowCmptRef.instance) {
      this._windowCmptRef.instance.hasBorder = show;
    }
  }

  overflowHidden(hidden: boolean = false): void {
    if (this._windowCmptRef && this._windowCmptRef.instance) {
      this._windowCmptRef.instance.overflowHidden = hidden;
    }
  }

  whiteCloseBtn(white: boolean = false, opacity: number = 1): void {
    if (this._windowCmptRef && this._windowCmptRef.instance) {
      this._windowCmptRef.instance.whiteCloseBtn = white;
      this._windowCmptRef.instance.opacity = opacity;
    }
  }

  resize(height: number | string = null, width: number = null): void {
    if (this._windowCmptRef && this._windowCmptRef.instance) {
      height && (this._windowCmptRef.instance.height = height);
      width && (this._windowCmptRef.instance.width = width);
      if (width || height) {
        this._windowCmptRef.instance.onResize();
      }
    }
  }

  /**
   * Closes the modal with an optional `result` value.
   *
   * The `DbMobalRef.result` promise will be resolved with the provided value.
   */
  close(result?: any): void {
    if (this._windowCmptRef) {
      this._closed.next(result);
      this._resolve(result);
      this._removeModalElements();
    }
  }

  private _dismiss(reason?: any) {
    this._dismissed.next(reason);
    this._reject(reason);
    this._removeModalElements();
  }

  /**
   * Dismisses the modal with an optional `reason` value.
   *
   * The `DbModalRef.result` promise will be rejected with the provided value.
   */
  dismiss(reason?: any): void {
    if (this._windowCmptRef) {
      if (!this._beforeDismiss) {
        this._dismiss(reason);
      } else {
        const dismiss = this._beforeDismiss();
        if (isPromise(dismiss)) {
          dismiss.then(
              result => {
                if (result !== false) {
                  this._dismiss(reason);
                }
              },
              () => {});
        } else if (dismiss !== false) {
          this._dismiss(reason);
        }
      }
    }
  }

  private _removeModalElements() {
    const windowTransition$ = this._windowCmptRef.instance.hide();
    const backdropTransition$ = this._backdropCmptRef ? this._backdropCmptRef.instance.hide() : of(undefined);

    // hiding window
    windowTransition$.subscribe(() => {
      if (!this._windowCmptRef || !this._windowCmptRef.location) return;

      const {nativeElement} = this._windowCmptRef && this._windowCmptRef.location;
      nativeElement && nativeElement.parentNode && nativeElement.parentNode.removeChild(nativeElement);
      this._windowCmptRef.destroy();

      if (this._contentRef && this._contentRef.viewRef) {
        this._contentRef.viewRef.destroy();
      }

      this._windowCmptRef = <any>null;
      this._contentRef = <any>null;
    });

    // hiding backdrop
    backdropTransition$.subscribe(() => {
      if (this._backdropCmptRef) {
        const {nativeElement} = this._backdropCmptRef.location;
        nativeElement && nativeElement.parentNode && nativeElement.parentNode.removeChild(nativeElement);
        this._backdropCmptRef.destroy();
        this._backdropCmptRef = <any>null;
      }
    });

    // all done
    zip(windowTransition$, backdropTransition$).subscribe(() => {
      this._hidden.next();
      this._hidden.complete();
    });
  }
}
